import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { 
  Search, 
  Loader2, 
  Calendar, 
  Clock, 
  User, 
  Mail,
  CheckCircle,
  XCircle,
  QrCode
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LookupBookingPage = () => {
  const [searchParams] = useSearchParams();
  const initialBooking = searchParams.get("booking") || "";

  const [bookingNumber, setBookingNumber] = useState(initialBooking);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!bookingNumber) {
      toast.error("Please enter a booking number");
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = email ? { email: email.toLowerCase() } : {};
      const res = await axios.get(`${API}/bookings/${bookingNumber}`, { params });
      setBooking(res.data);
    } catch (error) {
      console.error("Error fetching booking:", error);
      setBooking(null);
      toast.error("Booking not found. Please check your booking number.");
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
    completed: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    no_show: "bg-neutral-500/20 text-neutral-400 border-neutral-500/30",
  };

  const paymentColors = {
    pending: "bg-yellow-500/20 text-yellow-400",
    paid: "bg-green-500/20 text-green-400",
    failed: "bg-red-500/20 text-red-400",
    refunded: "bg-neutral-500/20 text-neutral-400",
  };

  return (
    <div className="container-empire max-w-2xl" data-testid="lookup-booking-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <span className="font-cookie text-3xl text-empire-gold block mb-2">
          Find your booking
        </span>
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          Lookup Booking
        </h1>
        <p className="text-neutral-400">
          Enter your booking reference to view details and get your QR code
        </p>
      </motion.div>

      {/* Search Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="card-empire mb-8">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="booking" className="text-white">Booking Reference</Label>
                <Input
                  id="booking"
                  type="text"
                  placeholder="EDK-20250101-ABC123"
                  value={bookingNumber}
                  onChange={(e) => setBookingNumber(e.target.value.toUpperCase())}
                  className="input-empire font-mono"
                  required
                  data-testid="input-booking-number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-empire"
                  data-testid="input-email"
                />
                <p className="text-xs text-neutral-500">
                  Enter your email for additional verification
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-empire"
                data-testid="search-booking-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Find Booking
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>

      {/* Results */}
      {searched && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {booking ? (
            <Card className="card-empire-featured overflow-hidden">
              <CardHeader className="bg-empire-gold/10 border-b border-empire-gold/20">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Booking Details</CardTitle>
                  <Badge className={`${statusColors[booking.status]} border`}>
                    {booking.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                {/* QR Code */}
                {booking.payment_status === "paid" && (
                  <div className="flex flex-col items-center mb-6 pb-6 border-b border-neutral-800">
                    <div className="bg-white p-4 rounded-2xl mb-3" data-testid="booking-qr-code">
                      <QRCodeSVG 
                        value={booking.qr_code_hash || booking.booking_number}
                        size={150}
                        level="H"
                        imageSettings={{
                          src: "/branding/logo-dark.png",
                          width: 30,
                          height: 30,
                          excavate: true,
                        }}
                      />
                    </div>
                    <p className="text-sm text-neutral-400 flex items-center gap-1">
                      <QrCode className="w-4 h-4" />
                      Show this for check-in
                    </p>
                  </div>
                )}

                {/* Booking Info */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Booking Number</span>
                    <span className="font-mono font-bold text-white">{booking.booking_number}</span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Guest</span>
                    <span className="text-white flex items-center gap-2">
                      <User className="w-4 h-4 text-empire-gold" />
                      {booking.guest_name}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Email</span>
                    <span className="text-white flex items-center gap-2">
                      <Mail className="w-4 h-4 text-empire-gold" />
                      {booking.guest_email}
                    </span>
                  </div>

                  {booking.schedule && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-400">Class</span>
                        <span className="text-white">{booking.dance_class?.name || "Dance Class"}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-neutral-400">Date</span>
                        <span className="text-white flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-empire-gold" />
                          {format(parseISO(booking.schedule.start_datetime), "EEE, MMM d, yyyy")}
                        </span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-neutral-400">Time</span>
                        <span className="text-white flex items-center gap-2">
                          <Clock className="w-4 h-4 text-empire-gold" />
                          {format(parseISO(booking.schedule.start_datetime), "h:mm a")}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                    <span className="text-neutral-400">Payment Status</span>
                    <Badge className={paymentColors[booking.payment_status]}>
                      {booking.payment_status === "paid" ? (
                        <><CheckCircle className="w-3 h-3 mr-1" /> Paid</>
                      ) : (
                        <><XCircle className="w-3 h-3 mr-1" /> {booking.payment_status}</>
                      )}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Amount</span>
                    <span className="text-xl font-bold text-empire-gold">
                      KES {booking.total_amount?.toLocaleString()}
                    </span>
                  </div>

                  {booking.checked_in_at && (
                    <div className="flex items-center justify-between pt-4 border-t border-neutral-800">
                      <span className="text-neutral-400">Checked In</span>
                      <span className="text-green-400 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        {format(parseISO(booking.checked_in_at), "MMM d, h:mm a")}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-empire">
              <CardContent className="p-8 text-center">
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Booking Not Found</h3>
                <p className="text-neutral-400">
                  We couldn't find a booking with that reference. Please check and try again.
                </p>
              </CardContent>
            </Card>
          )}
        </motion.div>
      )}
    </div>
  );
};
